import { useCallback, useEffect, useRef, useState } from 'react';
import apiV2 from './apiV2';
import { checklistError } from './dailyChecklist';

export function usePacedDay(section: number, date?: string, display = false) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef<any>(null);
  const mounted = useRef(true);
  const activeEndpoint = useRef('');
  const sending = useRef(false);
  const url = `/sections/${section}/paced-day/`;
  const query = `${date ? `date=${encodeURIComponent(date)}&` : ''}${display ? 'display=1' : ''}`;
  const endpoint = `${url}?${query}`;
  activeEndpoint.current = endpoint;
  const load = useCallback(async () => {
    if (!section) return;
    try {
      const response = await apiV2.get(endpoint);
      if (mounted.current && activeEndpoint.current === endpoint) { setData(response.data); setError(old => old.startsWith('Connection interrupted.') ? '' : old); }
    } catch { if (mounted.current && activeEndpoint.current === endpoint) setError('Connection interrupted. Showing the last saved classroom state.'); }
  }, [endpoint, section]);
  useEffect(() => {
    mounted.current = true; setData(null); pending.current = null;
    void load(); const interval = setInterval(load, 4000);
    const refresh = () => void load();
    window.addEventListener('paced-day-changed', refresh);
    window.addEventListener('daily-checklist-saved', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { mounted.current = false; clearInterval(interval); window.removeEventListener('paced-day-changed', refresh); window.removeEventListener('daily-checklist-saved', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [load]);
  const command = async (action: string, values: any = {}) => {
    if (sending.current) return false;
    sending.current = true;
    const signature = JSON.stringify({ action, ...values });
    if (!pending.current || pending.current.signature !== signature) pending.current = {
      signature, body: { action, ...values, revision: data?.revision || 0, request_id: crypto.randomUUID() },
    };
    setBusy(true); setError('');
    try {
      const response = await apiV2.post(endpoint, pending.current.body);
      if (mounted.current && activeEndpoint.current === endpoint) setData(response.data);
      pending.current = null;
      window.dispatchEvent(new Event('paced-day-changed'));
      return true;
    } catch (e: any) {
      if (e.response) pending.current = null;
      if (mounted.current && activeEndpoint.current === endpoint) setError(checklistError(e));
      if (e.response?.status === 409) { const r = await apiV2.get(endpoint).catch(() => null); if (r && mounted.current) setData(r.data); }
      return false;
    } finally { sending.current = false; if (mounted.current && activeEndpoint.current === endpoint) setBusy(false); }
  };
  return { data, error, busy, command, reload: load };
}

export function remaining(timer: any, now: number) {
  return timer?.state === 'running' && timer?.deadline
    ? Math.max(0, Math.ceil((Date.parse(timer.deadline) - now) / 1000))
    : Math.max(0, timer?.remaining_seconds || 0);
}

export function clockText(seconds: number) {
  return `${Math.floor(seconds / 60).toString().padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;
}
